import { useSelector, useDispatch } from 'react-redux';

// Types
import { Workspace } from 'Shared/types/Workspace.type';
import { WorkspaceTemplateType } from 'Shared/constants/types';

// Hooks
import { useToggle } from 'Shared/utils/hooks';

// Selectors
import * as WorkspaceTemplatesSelectors from 'Shared/selectors/WorkspaceTemplates.selectors';

// Actions
import { applyTemplateAction } from 'Shared/actions/workspaces';
import { fetchWorkspaceTemplates } from 'Shared/actions/WorkspaceTemplates.actions';
import { useEffect } from 'react';

type Props = {
  workspace: Workspace;
  type?: WorkspaceTemplateType;
  fetchOnMount?: boolean;
};

export const useWorkspaceTemplates = ({ workspace, type, fetchOnMount }: Props) => {
  const dispatch = useDispatch();

  const [isFetchingTemplates, togglesFetchingTemplates] = useToggle(false);
  const [isApplyingTemplate, toggleIsApplyingTemplate] = useToggle(false);

  const templates =
    useSelector(state => WorkspaceTemplatesSelectors.getWorkspaceTemplatesByType(state, { type }))
      .valueSeq()
      ?.toJS() || [];

  const handleFetchTemplates = ({ type }: { type?: WorkspaceTemplateType }) => {
    togglesFetchingTemplates();
    dispatch(fetchWorkspaceTemplates({ type })).finally(() => {
      togglesFetchingTemplates();
    });
  };

  const handleApplyTemplate = ({ template, sourcePage }: Partial<{ template?: string; sourcePage?: string }> = {}) => {
    toggleIsApplyingTemplate();
    dispatch(applyTemplateAction({ workspaceId: workspace.id, templateSlug: template, sourcePage })).then(() => {
      toggleIsApplyingTemplate();
    });
  };

  useEffect(() => {
    handleFetchTemplates({ type });
  }, [fetchOnMount]);

  return { templates, isFetchingTemplates, isApplyingTemplate, handleFetchTemplates, handleApplyTemplate };
};
